import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  FiX,
  FiCheckCircle,
  FiSearch,
  FiCalendar,
  FiUser,
  FiClock,
  FiTag
} from 'react-icons/fi';

const CompletedTasksModal = ({ isOpen, onClose, completedTasks, onTaskClick }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('completed_at');
  const [sortOrder, setSortOrder] = useState('desc');

  const priorityColors = {
    low: 'text-gray-600 bg-gray-100 dark:bg-gray-700 dark:text-gray-300',
    medium: 'text-yellow-700 bg-yellow-100 dark:bg-yellow-900/30 dark:text-yellow-300',
    high: 'text-orange-700 bg-orange-100 dark:bg-orange-900/30 dark:text-orange-300',
    urgent: 'text-red-700 bg-red-100 dark:bg-red-900/30 dark:text-red-300'
  };

  const filteredAndSortedTasks = useMemo(() => {
    let filtered = completedTasks.filter(task =>
      task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      task.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case 'title':
          aValue = a.title.toLowerCase();
          bValue = b.title.toLowerCase();
          break;
        case 'priority':
          const priorityOrder = { low: 1, medium: 2, high: 3, urgent: 4 };
          aValue = priorityOrder[a.priority] || 0;
          bValue = priorityOrder[b.priority] || 0;
          break;
        case 'completed_at':
        default:
          aValue = new Date(a.completed_at || a.updated_at);
          bValue = new Date(b.completed_at || b.updated_at);
          break;
      }

      if (sortOrder === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

    return filtered;
  }, [completedTasks, searchTerm, sortBy, sortOrder]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-2xl max-h-[90vh] bg-white dark:bg-gray-800 rounded-lg shadow-xl ring-1 ring-gray-200 dark:ring-gray-700 overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <FiCheckCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Completed Tasks
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {filteredAndSortedTasks.length} of {completedTasks.length} tasks
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            aria-label="Close"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Controls */}
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-col sm:flex-row gap-2">
            {/* Search */}
            <div className="flex-1 relative">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search completed tasks…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm text-gray-900 dark:text-white placeholder-gray-400"
              />
            </div>

            {/* Sort */}
            <div className="flex gap-1.5">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm text-gray-900 dark:text-white"
              >
                <option value="completed_at">Completed Date</option>
                <option value="title">Title</option>
                <option value="priority">Priority</option>
              </select>

              <button
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm text-gray-700 dark:text-gray-300"
                title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
              >
                {sortOrder === 'asc' ? '↑' : '↓'}
              </button>
            </div>
          </div>
        </div>

        {/* Task list */}
        <div className="flex-1 overflow-y-auto">
          {filteredAndSortedTasks.length === 0 ? (
            <div className="text-center py-10">
              <FiCheckCircle className="w-10 h-10 mx-auto mb-2 text-gray-300 dark:text-gray-600" />
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {searchTerm ? 'No completed tasks match your search' : 'No completed tasks yet'}
              </p>
            </div>
          ) : (
            <div className="p-4 space-y-2">
              {filteredAndSortedTasks.map((task) => (
                <div
                  key={task.id}
                  onClick={() => onTaskClick(task)}
                  className="p-3 bg-white dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-sm transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <FiCheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                    <h3 className="flex-1 font-medium text-sm text-gray-900 dark:text-white truncate">
                      {task.title}
                    </h3>
                    {task.priority && (
                      <span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded uppercase ${priorityColors[task.priority]}`}>
                        {task.priority}
                      </span>
                    )}
                  </div>

                  {task.description && (
                    <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2 mb-2 ml-6">
                      {task.description}
                    </p>
                  )}

                  <div className="flex items-center flex-wrap gap-3 text-[11px] text-gray-500 dark:text-gray-400 ml-6">
                    {task.completed_at && (
                      <div className="flex items-center gap-1">
                        <FiCalendar className="w-3 h-3" />
                        <span>Completed {new Date(task.completed_at).toLocaleDateString()}</span>
                      </div>
                    )}
                    {task.assignee && (
                      <div className="flex items-center gap-1">
                        <FiUser className="w-3 h-3" />
                        <span>{task.assignee.firstName} {task.assignee.lastName}</span>
                      </div>
                    )}
                    {task.estimated_hours && (
                      <div className="flex items-center gap-1">
                        <FiClock className="w-3 h-3" />
                        <span>{task.estimated_hours}h estimated</span>
                      </div>
                    )}
                  </div>

                  {task.tags && task.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5 ml-6">
                      {task.tags.slice(0, 3).map(tag => (
                        <span
                          key={tag.id}
                          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium"
                          style={{
                            backgroundColor: `${tag.color}20`,
                            color: tag.color
                          }}
                        >
                          <FiTag className="w-2.5 h-2.5" />
                          {tag.name}
                        </span>
                      ))}
                      {task.tags.length > 3 && (
                        <span className="text-[10px] text-gray-500 dark:text-gray-400">
                          +{task.tags.length - 3} more
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CompletedTasksModal;
